import { HttpClient } from "@angular/common/http";
import { sortByDateTime } from '@k12/app-services';
import { SignalRService } from '@k12/signalr';
import { inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, catchError, map, of, Subject, takeUntil, tap } from "rxjs";
import { AlertBanner, BannerConfig, BannerIcons, BannerTypeEnum, ToastMessageType } from '@k12/models';
import { ToastMessageService } from "./toast-message.service";

@Injectable({
  providedIn: 'root'
})
export class AlertBannerService implements OnDestroy {
  private static readonly MAX_TIMER_MS = 2_147_483_647;

  private readonly http = inject(HttpClient);
  private readonly signalRService = inject(SignalRService);
  private readonly ngDestroy: Subject<void> = new Subject<void>();
  private readonly toastMessageService = inject(ToastMessageService);
  public bannerConfig: BehaviorSubject<BannerConfig[]> = new BehaviorSubject<BannerConfig[]>([]);
  private adminUrl: string = "";
  private autoExpireBanners: { [id: string]: ReturnType<typeof setTimeout> } = {};

  constructor() {
    setTimeout(() => {
      this.registerSignalREvents();
    },100);
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
    Object.values(this.autoExpireBanners).forEach(banner => clearTimeout(banner));
  }

  
  public getAlertBanners(url: string): void {
    this.adminUrl =  url;
    this.http
      .get<AlertBanner[]>(`${url}/comms/AlertBannersByAccountId`)
      .pipe(
        map((res : AlertBanner[]) => this.mapToBannerConfigs(res)), 
        tap((alertBanners : BannerConfig[]) => this.bannerConfig.next(alertBanners)),
        catchError(() => {
          this.toastMessageService.showToast(ToastMessageType.Error,"We couldn't load your alert banners. Please refresh and try again.");
          this.bannerConfig.next([]);
          return of<BannerConfig[]>([]);
        })
      ).subscribe();
  }
    
  private mapToBannerConfigs(res: AlertBanner[]): BannerConfig[] {
    const sortedAlertBanners = res.length == 0 ? [] : sortByDateTime(res, 'sentDate');
    if(sortedAlertBanners.length > 0)
    {
      this.setAutoExpire(sortedAlertBanners);
    }
    return (sortedAlertBanners).map<BannerConfig>((x) => ({
      id: x.id,
      icon: BannerIcons[this.getAlertBannerType(x.alertBannerTypeId)],
      message: x.bannerBody ?? '',
      title: x.bannerSubject ?? '',
      type: { type: this.getAlertBannerType(x.alertBannerTypeId) },
      sendDate: x.sentDate,
      endDate: x.endDate
    }));
  }

  private getAlertBannerType(bannerTypeId : number) : BannerTypeEnum {
    let bannerType = BannerTypeEnum.INFO;
      switch (bannerTypeId) {
        case 1:
          bannerType = BannerTypeEnum.INFO;
          break;
        case 2:
          bannerType = BannerTypeEnum.SUCCESS;
          break;
        case 3:
          bannerType = BannerTypeEnum.WARNING;
          break;
        case 4:
          bannerType = BannerTypeEnum.ERROR;
          break;
      }
    return bannerType;
  }

  public setAlertBannerReadDate(alertBannerId: string): void {
    this.http
      .put(`${this.adminUrl}/comms/SetAlertBannerReadDate/${alertBannerId}`, null)
      .pipe(
        tap(() => {
          const currentBanners = this.bannerConfig.value;
          const updatedBanners = currentBanners.filter((b: BannerConfig) => b.id !== alertBannerId);
          const sortedAlertBanners = sortByDateTime(updatedBanners, 'sendDate');
          this.bannerConfig.next(sortedAlertBanners);
        }),
        catchError(() => {
          this.toastMessageService.showToast(ToastMessageType.Error,"We couldn't update your alert banner. Please refresh and try again.");
          return of(null); 
        })
      ).subscribe();
  }

  public dismissBanner(banner: BannerConfig): void {
    this.setAlertBannerReadDate(banner.id);

    if (this.autoExpireBanners[banner.id]) {
        clearTimeout(this.autoExpireBanners[banner.id]);
        delete this.autoExpireBanners[banner.id];
    }
  }

  private registerSignalREvents() : void {
      this.signalRService.signalRConnectionStatus.pipe(takeUntil(this.ngDestroy)) .subscribe((res) => {
          if (res === 'open') {
            this.attachSignalREvents();
          } else {
            this.detachSignalREvents();
            }
      });
    }

    private attachSignalREvents(): void {
      this.signalRService.hubConnection?.off('AlertBanner');
      this.signalRService.hubConnection?.on('AlertBanner', (alertBannerReceived) => {
        const currentBanners = this.bannerConfig.value;
        const alertBanner: AlertBanner = JSON.parse(alertBannerReceived);
        if (currentBanners.some((banner) => banner.id === alertBanner.id)) {
          return;
        }
        const alertBannerType = this.getAlertBannerType(alertBanner.alertBannerTypeId);
        const newalertBanner =  {
            id: alertBanner.id,
            type: { type: alertBannerType },
            message: alertBanner.bannerBody,
            title:alertBanner.bannerSubject,
            icon: BannerIcons[alertBannerType],
            sendDate: alertBanner.sentDate
          };
        const updatedBanners = [...currentBanners, newalertBanner];
        const sortedAlertBanners = sortByDateTime(updatedBanners, 'sendDate');
        this.bannerConfig.next(sortedAlertBanners);
        this.setAutoExpire([alertBanner]);
      });

      // Listen for active alert banner removals from SignalR and update the bannerConfig accordingly.
      this.signalRService.hubConnection?.on('RemoveAlertBanner', (alertBannerId: string) => {
        const currentBanners = this.bannerConfig.value;
        const updatedBanners = currentBanners.filter((b: BannerConfig) => b.id !== alertBannerId);
        const sortedAlertBanners = sortByDateTime(updatedBanners, 'sendDate');
        this.bannerConfig.next(sortedAlertBanners);

        if (this.autoExpireBanners[alertBannerId]) {
          clearTimeout(this.autoExpireBanners[alertBannerId]);
          delete this.autoExpireBanners[alertBannerId];
        }
      });
    }

    private detachSignalREvents(): void {
      this.signalRService.hubConnection?.off('AlertBanner');
      this.signalRService.hubConnection?.off('RemoveAlertBanner');
    }

    private setAutoExpire(alertBanners: AlertBanner[]): void {
      for (const alertBanner of alertBanners) {
        if (this.autoExpireBanners?.[alertBanner.id]) {
          clearTimeout(this.autoExpireBanners[alertBanner.id]);
          delete this.autoExpireBanners[alertBanner.id];
        }
        
        // if has end date then set auto expire
        if(alertBanner.endDate)
        {
          const endTime = new Date(alertBanner.endDate)

          if (!endTime || Number.isNaN(endTime.getTime())) {
            continue;
          }
          // After 1 minute of end time
          const expireAtMs = endTime.getTime() + 60_000;
          this.scheduleAutoExpire(alertBanner.id, expireAtMs);
        }
      }
  }

  private scheduleAutoExpire(bannerId: string, expireAtMs: number): void {
      const remainingMs = expireAtMs - Date.now();

      if (remainingMs <= 0) {
        this.autoExpireBanner(bannerId);
        return;
      }

      const delay = Math.min(remainingMs, AlertBannerService.MAX_TIMER_MS);
      this.autoExpireBanners[bannerId] = setTimeout(() => {
        this.scheduleAutoExpire(bannerId, expireAtMs);
      }, delay);
  }

  private autoExpireBanner(bannerId: string): void {
      const currentBanners = this.bannerConfig.value;
      const updatedBanners = currentBanners.filter((b: BannerConfig) => b.id !== bannerId);
      const sortedAlertBanners = sortByDateTime(updatedBanners, 'sendDate');
      this.bannerConfig.next(sortedAlertBanners);
      delete this.autoExpireBanners[bannerId];
  }
}